import React from "react";
import { Star, Quote } from "lucide-react";
import CtaBand from "../components/CtaBand";
import ReviewWall from "../components/ReviewWall";
import useSeo from "../hooks/useSeo";
import useReveal from "../hooks/useReveal";
import { testimonialsPage, seo, badges, social, googleReviewUrl } from "../data/siteData";

export default function Testimonials() {
  useSeo(seo["/testimonials"].title, seo["/testimonials"].description);
  useReveal();
  return (
    <>
      <section className="border-b-4 border-[#244aa2] bg-[#f7f7f7] py-12">
        <div className="mr-container">
          <h1 className="mr-h1 text-3xl sm:text-4xl">{testimonialsPage.h1}</h1>
          <h2 className="font-head mt-3 text-[15px] font-bold uppercase tracking-wide text-[#a10000]">
            Refinishing Testimonials
          </h2>
          <p className="mt-4 max-w-3xl leading-relaxed text-[#575757]">{testimonialsPage.intro}</p>
          <div className="mt-6 flex flex-wrap items-center gap-6">
            <a href={social[2].url} target="_blank" rel="noreferrer">
              <img
                src={badges.bbbHorz}
                alt="BBB Accredited Business"
                className="h-12 w-auto transition-transform duration-200 hover:scale-105"
              />
            </a>
            <a href={social[3].url} target="_blank" rel="noreferrer">
              <img
                src={badges.angiesList}
                alt="Angie's List"
                className="h-12 w-auto transition-transform duration-200 hover:scale-105"
              />
            </a>
            <a
              href={googleReviewUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="testimonials-write-review-link"
              className="bg-[#244aa2] px-6 py-2.5 text-[12px] font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-[#0c2a66]"
            >
              Write a Review
            </a>
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mr-container-wide space-y-8">
          {testimonialsPage.items.map((t) => (
            <article
              key={t.author}
              className="reveal border-l-4 border-[#244aa2] bg-[#f7f7f7] p-7 shadow-sm transition-shadow duration-300 hover:shadow-lg"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="mr-h3 text-lg">{t.author}</h3>
                  <p className="mt-1 text-[13px] font-bold uppercase tracking-wide text-[#a10000]">
                    Description of work
                  </p>
                  <p className="mt-1 text-[15px] italic text-[#575757]">{t.work}</p>
                </div>
                <div className="flex shrink-0 gap-0.5">
                  {[0, 1, 2, 3, 4].map((s) => (
                    <Star key={s} className="h-4 w-4 fill-[#f0a92b] text-[#f0a92b]" />
                  ))}
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                <Quote className="h-6 w-6 shrink-0 text-[#244aa2]/40" />
                <p className="text-[15.5px] leading-relaxed text-[#575757]">{t.body}</p>
              </div>
              {t.beforeAfter && (
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {t.beforeAfter.map((b) => (
                    <figure key={b.label} className="overflow-hidden bg-white shadow-sm">
                      <img
                        src={b.image}
                        alt={`${t.author} countertop resurfacing — ${b.label}`}
                        loading="lazy"
                        className="h-56 w-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      <figcaption className="bg-[#244aa2] py-2 text-center text-[12px] font-bold uppercase tracking-widest text-white">
                        {b.label}
                      </figcaption>
                    </figure>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </section>

      <ReviewWall />
      <CtaBand headline="Join hundreds of happy Connecticut homeowners" />
    </>
  );
}
